import { FC, useEffect, useMemo, useState } from "react";
import {
  Loading,
  CoachRun,
  CoachRunDataSource,
  CoachRunResult,
  Notification,
  Modal,
  Button,
} from "@quark/design";
import { getChallengeRef, submitChallenge } from "../../api";
import { Numerical } from "../../types";
import { CodePrompt, ChallengePrompt } from "../../types/code";
import CodeErrorHelp from "../code-error-help";
import CodeOutputHelp from "../code-output-help";
import CodeLogs from "./code-logs";
import useChallengeContext from "../../hooks/use-challege-context";
import { useLocation, useNavigate } from "react-router-dom";
import CoachPunch from "./coach-punch";
import useDocumentTitle from "../../hooks/use-document-title";
import styles from "./index.module.scss";
import { HIDE_ASSISTANT_EVENT } from "../../constants";
import CoachAnswer from "./answer";
import { schoolPride } from "@quark/animation-effect";

interface CoachPageProps {
  challengeId: Numerical;
  visible: boolean;
  hideAnswer?: boolean;
  onSubmitReturn?: (result: CoachRunResult) => void;
  mode?: string;
  onClose: () => void;
}

const CoachPage: FC<CoachPageProps> = ({
  challengeId,
  visible,
  hideAnswer = false,
  onSubmitReturn = () => { },
  onClose,
  mode = "尝试一下",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchStr = useMemo(() => {
    return location.search.replace(/^\?/, "");
  }, [location.search]);
  const showAnswer = useMemo(() => {
    return !location.pathname.includes("/course");
  }, [location]);
  const [dataSource, setDataSource] = useState<CoachRunDataSource>(
    {} as CoachRunDataSource
  );

  useDocumentTitle(`${mode}-${dataSource?.title}`);

  const [dataResult, setDataResult] = useState<CoachRunResult | undefined>(
    undefined
  );

  const [userCode, setUserCode] = useState("");
  const [testCaseErrPrompt, setTestCaseErrPrompt] = useState<CodePrompt>();
  const [testCaseOutputPrompt, setTestCaseOutputPrompt] =
    useState<ChallengePrompt>();
  const [errHelpVisible, setErrHelpVisible] = useState(false);
  const [outputHelpVisible, setOutputHelpVisible] = useState(false);
  const [punchVisible, setPunchVisible] = useState(false);

  const [loading, setLoading] = useState(false);
  const loaded = useMemo(() => {
    return !!dataSource.challenge_id;
  }, [dataSource]);

  const [running, setRunning] = useState(false);

  const { context, catalog } = useChallengeContext();

  const getCoachData = (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    getChallengeRef(challengeId)
      .then((data) => {
        setDataSource(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const runCode = (code: string | undefined, language: string) => {
    if (!code || !code.trim()) {
      Notification.error("请输入有效的代码", { duration: 2000 });
      return;
    }
    setUserCode(code);
    const params = {
      challenge_id: challengeId,
      source_code: code,
      language: language,
    };
    setRunning(true);
    submitChallenge(params)
      .then((data) => {
        setDataResult(data);
        onSubmitReturn(data);
        if (data.is_pass) {
          schoolPride();
          getCoachData(true);
          setPunchVisible(true);
          window.dispatchEvent(new Event(HIDE_ASSISTANT_EVENT));
        }
      })
      .catch((e) => {
        if (e.message) {
          Notification.error(e.message, { duration: 2000 });
        }
        const { code } = e?.response?.data || {};
        if (code === 401) {
          Notification.error("登陆已失效");
          setTimeout(() => {
            Notification.dispose();
          }, 2000);
        }
      })
      .finally(() => {
        setRunning(false);
      });
  };

  const showTestCaseHelp = (index: number) => {
    const template = dataSource.code_templates[0];
    const { language } = template;
    const testCase = dataResult?.test_results[index];
    const {
      stderr = "",
      input = "",
      actual_output = "",
      expected_output = "",
    } = testCase || {};

    if (stderr) {
      setTestCaseErrPrompt({
        language,
        source_code: userCode,
        mode: "help",
        stderr,
        stdin: input,
        stdout: actual_output,
      });
      setErrHelpVisible(true);
    } else {
      setTestCaseOutputPrompt({
        challenge_id: Number(challengeId),
        language,
        source_code: userCode,
        test_result: {
          actual_output,
          expected_output,
          input,
        },
      });
      setOutputHelpVisible(true);
    }
  };

  const nextChallenge = () => {
    const { nxt_id } = context;
    if (nxt_id) {
      navigate(`/problemset/${catalog}/code/${context.nxt_id}?${searchStr}`);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    getCoachData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  return (
    <>
      {loading && <Loading fullscreen />}
      {loaded && !loading && (
        <CoachRun
          visible={visible}
          hideAnswer={hideAnswer}
          running={running}
          dataSource={dataSource}
          dataResult={dataResult}
          onClose={(tag) => {
            if (tag === "success") {
              nextChallenge();
            } else {
              onClose();
            }
          }}
          onRun={runCode}
          logContent={<CodeLogs challengeId={challengeId} />}
          answerContent={
            showAnswer && <CoachAnswer challengeId={Number(challengeId)} />
          }
          extraActions={
            <>
              {!!context.rnd_id && (
                <Button
                  onClick={() =>
                    navigate(
                      `/problemset/${catalog}/code/${context.rnd_id}?${searchStr}`
                    )
                  }
                >
                  随机一题
                </Button>
              )}
              {!!context.pre_id && (
                <Button
                  onClick={() =>
                    navigate(
                      `/problemset/${catalog}/code/${context.pre_id}?${searchStr}`
                    )
                  }
                >
                  上一题
                </Button>
              )}
              {!!context.nxt_id && (
                <Button onClick={nextChallenge}>下一题</Button>
              )}
            </>
          }
          onTestCaseHelpClick={(index: number) => showTestCaseHelp(index)}
        />
      )}

      <Modal
        title="AI答疑"
        visible={errHelpVisible}
        draggable
        onClose={() => setErrHelpVisible(false)}
      >
        <div
          style={{
            height: 400,
            width: 580,
            padding: "0 15px",
            overflow: "auto",
          }}
        >
          {!!testCaseErrPrompt && <CodeErrorHelp prompt={testCaseErrPrompt} />}
        </div>
      </Modal>

      <Modal
        title="AI答疑"
        visible={outputHelpVisible}
        draggable
        onClose={() => setOutputHelpVisible(false)}
      >
        <div
          style={{
            height: 400,
            width: 580,
            padding: "0 15px",
            overflow: "auto",
          }}
        >
          {!!testCaseOutputPrompt && (
            <CodeOutputHelp prompt={testCaseOutputPrompt} />
          )}
        </div>
      </Modal>

      <Modal
        className={styles.punchModal}
        visible={punchVisible}
        backdrop={false}
        onClose={() => setPunchVisible(false)}
      >
        {!!dataSource && (
          <div>
            <CoachPunch
              dataSource={dataSource}
              onPunch={() => setPunchVisible(false)}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default CoachPage;
